// Cycle-16 reply-quote primitive. Mirrors `UpdatedReplyPreviewCard`
// (iOS-A) / Android `UpdatedReplyPreviewCard`. Variants come from the
// Figma `Updated Reply` (`3412:5059`) + the `Reply` component set
// (`2918:10454`) — `1 person`, `2 people`, `3+ people`. The host code
// supplies the senders array; the preview text is treated as opaque
// display content (already-decrypted at the host) and is never logged.

import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';

export { REPLY_QUOTE_VARIANTS, type ReplyQuoteVariant } from './ReplyQuoteVariant';
import type { ReplyQuoteVariant } from './ReplyQuoteVariant';

export interface ReplyQuoteSenderRef {
  id: string;
  displayName: string;
  accentColor?: string | undefined;
}

export interface ReplyQuoteProps {
  variant: ReplyQuoteVariant;
  senders: ReadonlyArray<ReplyQuoteSenderRef>;
  preview: string;
  isSelf: boolean;
  onPress?: (() => void) | undefined;
  testID?: string | undefined;
}

const MAX_VISIBLE_NAMES = 3;

export const ReplyQuote = React.memo<ReplyQuoteProps>(
  ({ variant, senders, preview, isSelf, onPress, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const accentColor = useMemo(() => {
      const first = senders[0];
      return first?.accentColor ?? colors.accent.indigo;
    }, [senders, colors.accent.indigo]);

    const senderLine = useMemo(() => {
      if (senders.length === 0) {
        return 'Reply';
      }
      if (variant === 'singleSender') {
        const first = senders[0];
        return first ? first.displayName : 'Reply';
      }
      if (variant === 'twoSenders' && senders.length >= 2) {
        const first = senders[0];
        const second = senders[1];
        if (first && second) {
          return `${first.displayName} & ${second.displayName}`;
        }
      }
      const visible = senders.slice(0, MAX_VISIBLE_NAMES).map((sender) => sender.displayName);
      const remainder = senders.length - visible.length;
      if (remainder > 0) {
        return `${visible.join(', ')} and ${remainder} more`;
      }
      return visible.join(', ');
    }, [variant, senders]);

    const containerStyle = isSelf
      ? [styles.container, styles.containerSelf, { backgroundColor: colors.surface.elevated }]
      : [styles.container, styles.containerOther, { backgroundColor: colors.surface.elevated }];

    const senderTextStyle: TextStyle = {
      color: accentColor,
      fontWeight: '600',
    };

    const previewTextStyle: TextStyle = {
      color: colors.text.secondary,
    };

    const content = (
      <View style={containerStyle} testID={testID}>
        <View style={[styles.bar, { backgroundColor: accentColor }]} />
        <View style={styles.body}>
          <Text style={[styles.senderText, senderTextStyle]} numberOfLines={1}>
            {senderLine}
          </Text>
          <Text style={[styles.previewText, previewTextStyle]} numberOfLines={2}>
            {preview}
          </Text>
        </View>
      </View>
    );

    if (!onPress) {
      return content;
    }

    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel="Open replied message"
      >
        {content}
      </Pressable>
    );
  },
);

ReplyQuote.displayName = 'ReplyQuote';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    overflow: 'hidden',
  },
  containerSelf: {
    alignSelf: 'flex-end',
  },
  containerOther: {
    alignSelf: 'flex-start',
  },
  bar: {
    width: 3,
    borderRadius: tokens.radius.xs,
    marginRight: tokens.spacing.sm,
  },
  body: {
    flexShrink: 1,
  },
  senderText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    lineHeight: tokens.typography.fontSize.footnote * tokens.typography.lineHeight.normal,
  },
  previewText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.footnote,
    lineHeight: tokens.typography.fontSize.footnote * tokens.typography.lineHeight.normal,
    marginTop: 2,
  },
});
